import { Injectable, computed, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { forkJoin, from, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { Culture, Cluster, DataProcessingService, LanguageService, MediaService, CultureScore, CultureVariant } from '@frontend/common';
import { GeoJsonFeature } from '../maps';
import { GeoJsonFeatureGeometry } from '../maps/geojson-feature-geometry.model';
import { ClusterService } from '../clusters/cluster.service';
import { CultureClusterIdentifier } from '../clusters/culture-cluster-identifier.model';
import { CultureClusterWrapper } from '../clusters/culture-cluster-wrapper.model';
import { Tag } from '@frontend/core';
import { ErrorService } from '../error';
import { Vote } from '../votes';
import { CultureResponse } from './culture-response.model';

type GeoJsonResponseCulture = {
  properties: {    
    identifier : any, // iso_3166_2 in the case of cultures,
    className : string, // 'Culture', name of the model in the backend
    category : string, // 'geographic' // same as the culture's category
    type : string, // 'national' // same as the culture's type
    geometry_type : string, // 'geometry_110'
    displayOverDateLineInFarEast : boolean, // for small islands we want to display with the rest of Polynesia, not split by the international dateline
  },
  geometry: GeoJsonFeatureGeometry,
  geometry_meta? : any
}
export type CultureWrapperWithProperties = {
  properties: {
    culture: Culture,
    [key:string]: any
  },
}
export type CultureVariantFilter = {
  key: string,
  category: string,
  type: string,
  translationKeySingular: string,
  translationKeyPlural: string,
  translationScope: string,
}
interface CulturesWithVariantsResponse extends CultureResponse {
    variants : CultureVariant[];
}

@Injectable({
  providedIn: 'root',
})
export class CultureService {
  activeLanguageSubscription: Subscription;
  private _loadingObject = signal<{[key:string]:boolean}>({});
  public readonly loadingObject = this._loadingObject.asReadonly();

  // Locale-keyed cache of translated cultures: one list per UI language, held in
  // parallel and never flushed on locale change, so switching back to a
  // previously-viewed language is instant and free. Only the *translated* culture
  // data is keyed here — the heavy geometry (`culturesGeoJson`, below) is
  // locale-independent and deliberately NOT keyed or refetched on locale change.
  private _culturesByLocale = signal<Record<string, Culture[]>>({});
  /** The cultures for the currently-active UI language (empty until fetched). */
  readonly activeCultures = computed<Culture[]>(
    () => this._culturesByLocale()[this.languageService.activeLanguage().languageKey] ?? [],
  );
  /** Active-locale slice as an observable, for reactive consumers (toSignal / pipe). */
  public readonly cultures$: Observable<Culture[]>;
  // Distinct plain culture filters (`category::type`) ever requested. Replayed into
  // a newly-active locale so reactive `cultures$` consumers stay populated.
  private _requestedFilters = new Set<string>();
  // In-flight plain-culture requests keyed `locale::category::type`, so service
  // repopulation and a component's own refetch on the same locale change share one
  // HTTP call rather than issuing duplicates.
  private _inFlightCultures = new Map<string, Observable<Culture[]>>();

  // Heavy geo geometry (geometry_110 etc.), keyed by iso identifier and treated as
  // effectively static. Locale-independent — never keyed by locale and never
  // refetched on locale change. Persisted separately in CacheStorage (see getGeoJson).
  culturesGeoJson : GeoJsonFeature[] = [];
  private _variantFilters : CultureVariantFilter[] = [];
  private maximumCacheAges = [
    {cacheKey : 'geojson-cache', maxAge : 1000 * 60 * 60 * 24 * 730}, // two years
    {cacheKey : 'benchmark_cultures-frequent', maxAge : 1000 * 60 * 60 * 24 * 30}, // a month
  ]

  constructor(
    private languageService: LanguageService,
    private clusterService: ClusterService,
    private http: HttpClient,
    private dataProcessingService : DataProcessingService,
    private mediaService : MediaService,
    private errorService: ErrorService,
  ) 
  {
    this.cultures$ = toObservable(this.activeCultures);
    // On a genuine locale change, keep the active locale populated by replaying the
    // culture filters already requested — but only if the feature is in use. Other
    // locales are left intact (never flushed), so switching back is instant and free.
    this.activeLanguageSubscription =
      this.languageService.activeLanguageChanges$.subscribe(() => this.repopulateActiveLocale());
    const variantFilterData = [
      ['geography', 'column', 'geography'],
      ['ethnolinguistic', 'column', 'population'],
      ['class', 'community', 'community'],
      ['class', 'stratum', 'class'],
      ['generation', 'stratum', 'generation'],
    ];

    this._variantFilters = variantFilterData.map(([category, type, key]) => ({
      key,
      category,
      type,
      translationKeySingular: `diversity.${key}.singular`,
      translationKeyPlural: `diversity.${key}.plural`,
      translationScope: 'diversity',
    }));
  }

  /** The cultures for the currently-active UI language. */
  get cultures(): Culture[] {
    return this.activeCultures();
  }

  private get activeLanguageKey(): string {
    return this.languageService.activeLanguage().languageKey;
  }

  private filterKey(category: string, type: string): string {
    return `${category ?? ''}::${type ?? ''}`;
  }

  /**
   * Keep the newly-active locale populated by replaying every plain culture filter
   * requested so far. Runs only when the feature is already in use (some locale
   * cached); a filter already present for the active locale returns from cache with
   * no HTTP, so switching back to a viewed locale is free.
   */
  private repopulateActiveLocale(): void {
    if (Object.keys(this._culturesByLocale()).length === 0) {
      return;
    }
    this._requestedFilters.forEach((fk) => {
      const [category, type] = fk.split('::').map((part) => (part === '' ? null : part));
      this.getCultures(category, type, false).subscribe({ error: () => void 0 });
    });
  }

  get variantFilters() : CultureVariantFilter[] {
    return this._variantFilters;
  }
  cacheCultures(cultures:Culture[]) : Culture[] {

    const key = this.activeLanguageKey;
    const existing = this._culturesByLocale()[key] ?? [];
    let merged = existing.length
      ? this.dataProcessingService.mergeTwoArraysWithUniqueIdentifiersAvoidingDuplicates(cultures, existing, 'id')
      : cultures;
    merged = [...merged].sort((a, b) => a.name.localeCompare(b.name));
    this._culturesByLocale.update((byLocale) => ({ ...byLocale, [key]: merged }));
    return merged;

  }
  convertCultureToTag (culture : Culture, preserveId : boolean, flagTransformations = 'w_32,c_fill,ar_1:1,r_max/'){
    return new Tag(
      preserveId ? culture.id : 0,
      culture.name,
      culture.slug,
      'culture',
      culture.flag?.emoji ? 'emoji' : culture.flag?.hash ? 'cloudinary_flag' : '',
      culture.flag?.emoji ?? culture.flag?.hash ?? '',
      culture.flag?.hash ? this.mediaService.getFlagUrlFromHash(culture.flag.hash,'',true,'.png') : ''
    )
  }
  convertCulturesToTags (cultures : Culture[], preserveId : boolean = false, flagTransformations? : string){
    if(!cultures.length){return [];};
    return cultures.map(c => this.convertCultureToTag(c,preserveId,flagTransformations));
  }
  transformGeoJsonFeature (itemFromBackend : GeoJsonResponseCulture) : GeoJsonFeature {

    return {
      properties : {...itemFromBackend.properties, name: null, culture: null, /* TODO  bounding box etc from itemFromBackend.geometry_meta */},
      type: "Feature",
      geometry: itemFromBackend.geometry
  }

  }
  transformGeoJsonArray (arrayFromBackend : GeoJsonResponseCulture[]) : GeoJsonFeature[]{
    let transformedCulturesGeoJson : GeoJsonFeature[] = arrayFromBackend.map(cgj=>{
      return this.transformGeoJsonFeature(cgj)
    });
    return transformedCulturesGeoJson;
  }

  cacheGeoJson(culturesGeoJson:GeoJsonFeature[]){
  
    if (this.culturesGeoJson.length){
      culturesGeoJson.forEach(newCgj => {
        let foundIndex = this.culturesGeoJson.findIndex(oldCgj => 
          oldCgj.properties.category === newCgj.properties.category &&
          oldCgj.properties.type === newCgj.properties.type &&
          oldCgj.properties.geometry_type === newCgj.properties.geometry_type
          );
        if (foundIndex >-1){
          this.culturesGeoJson[foundIndex] = newCgj;
        } else {
          this.culturesGeoJson.push(newCgj);
        }
        
      });
    } else {
      this.culturesGeoJson = culturesGeoJson;
    }

  }

  async cacheGeoJsonInCacheStorage(culturesGeoJson: GeoJsonFeature[]): Promise<void> {
    try {
      const cache = await caches.open('geojson-cache');
      const response = new Response(JSON.stringify(culturesGeoJson));
      await cache.put('cultures-geojson', response);
    } catch (error) {
      console.error('Error caching GeoJSON data:', error);
    }
  }

  async getCachedGeoJsonFromCacheStorage(): Promise<GeoJsonFeature[]> {
    try {
      const cache = await caches.open('geojson-cache');
      const responsePromise = cache.match('cultures-geojson'); // Don't await here
      const response = await responsePromise; // Await the response outside
      if (response) {
        const json = await response.json();
        return json as GeoJsonFeature[];
      }
    } catch (error) {
      console.error('Error retrieving cached GeoJSON data:', error);
    }
    return []; // Return empty array if no data found or an error occurred
  }

  private filterStoredGeoJson(culturesGeoJson : GeoJsonFeature[], category : string,type : string, geometry_type : string) {

    let filteredGeoJsonCultures : GeoJsonFeature[] = [];

    if (category !== null && type !== null && geometry_type !== null) {
      filteredGeoJsonCultures = culturesGeoJson.filter(feature => feature.properties.category === category && feature.properties.type === type && feature.properties.geometry_type === geometry_type);

    } else if (category !== null && type !== null) {
      filteredGeoJsonCultures = culturesGeoJson.filter(feature => feature.properties.category === category && feature.properties.type === type && feature.properties.geometry_type === null);

    } else if (category !== null && geometry_type !== null) {
      filteredGeoJsonCultures = culturesGeoJson.filter(feature => feature.properties.category === category && feature.properties.type === null && feature.properties.geometry_type === geometry_type);

    } else if (type !== null && geometry_type !== null) {
      filteredGeoJsonCultures = culturesGeoJson.filter(feature => feature.properties.category === null && feature.properties.type === type && feature.properties.geometry_type === geometry_type);

    } else if (category !== null) {
      filteredGeoJsonCultures = culturesGeoJson.filter(feature => feature.properties.category === category && feature.properties.type === null && feature.properties.geometry_type === null);

    } else if (type !== null) {
      filteredGeoJsonCultures = culturesGeoJson.filter(feature => feature.properties.category === null && feature.properties.type === type && feature.properties.geometry_type === null);

    } else if (geometry_type !== null) {
      filteredGeoJsonCultures = culturesGeoJson.filter(feature => feature.properties.category === null && feature.properties.type === null && feature.properties.geometry_type === geometry_type);
    }
    return filteredGeoJsonCultures;
  }

  async getGeoJson(category: string, type: string, geometry_type: string, freshFromServer: boolean): Promise<GeoJsonFeature[]> {
    const geoJsonFromCache: GeoJsonFeature[] = await this.getCachedGeoJsonFromCacheStorage();
  
    let filteredGeoJsonCultures: GeoJsonFeature[] = [];
  
    if (geoJsonFromCache?.length) {
      filteredGeoJsonCultures = this.filterStoredGeoJson(geoJsonFromCache, category, type, geometry_type);
    }
  
    if (filteredGeoJsonCultures?.length && !freshFromServer) {
      return filteredGeoJsonCultures;
    }
  
    let url = 'api/v1/cultures-geojson?';
    url = category ? url += 'category=' + category : url;
    url = type ? url += '&type=' + type : url;
    url = geometry_type ? url += '&geometry_type=' + geometry_type : url;
  
    const response = await this.http.get<{ data: GeoJsonResponseCulture[] }>(url).toPromise();
    if (response?.data?.length) {
      const transformedResponseItems: GeoJsonFeature[] = this.transformGeoJsonArray(response.data);
      this.cacheGeoJsonInCacheStorage(transformedResponseItems);
      return this.filterStoredGeoJson(transformedResponseItems, category, type, geometry_type);
    }
  }
  getClusters(category : string, type : string, freshFromServer: boolean){

    return this.clusterService.getClusters(category,type, freshFromServer);

  }
  // getClustersContainingCulture(culture:Culture, freshFromServer: boolean){
  //   return this.clusterService.getClustersContainingCulture(culture,freshFromServer);
  // }
  prepareGeoJsonDataForMap(cultureWrappers:CultureWrapperWithProperties[],plainGeoJsonFeatures: GeoJsonFeature[]){
    if (!cultureWrappers || !plainGeoJsonFeatures){return []};
    let availableCultureIdentifiers = cultureWrappers.map(c=>c.properties.culture?.iso);


    return plainGeoJsonFeatures.filter(f=>availableCultureIdentifiers.includes(f.properties.identifier) && f.properties.className === 'Culture')
      .map(feature => {
        const copiedFeature = { ...feature };
      
        let copiedProperties = { ...copiedFeature.properties };
      
        const cultureWrapper = cultureWrappers.find(c=>c.properties.culture?.iso===feature.properties.identifier);

        if (cultureWrapper){

          copiedProperties = { ...copiedProperties, ...cultureWrapper.properties }

        }
      
        copiedFeature.properties = copiedProperties;
      
        return copiedFeature;
      });
      
  }
  async getCulturesAndGeoJson(category: string, type: string, geometry_type: string, freshFromServer: boolean): Promise<[Culture[], GeoJsonFeature[]]> {
    const cultures$ = this.getCultures(category, type, freshFromServer).toPromise();
    const geoJson$ = this.getGeoJson(category, type, geometry_type, freshFromServer);
    const geoJsonFeatures: GeoJsonFeature[] = await geoJson$;
  
    return [await cultures$, geoJsonFeatures];
  }
  
  prepareCultureClusters(cultures:Culture[],clusters:Cluster[],relationships:CultureClusterIdentifier[], cluster_category: string, cluster_type: string) : CultureClusterWrapper[] {
    if(!cultures?.length || !clusters?.length || !relationships?.length){return [];};
    let filteredRelationships : CultureClusterIdentifier[] = cluster_category ? relationships.filter(r=>r.category === cluster_category): relationships;
    filteredRelationships = cluster_type ? relationships.filter(r=>r.type === cluster_type) : filteredRelationships;
    return filteredRelationships.map(r => {
      return new CultureClusterWrapper (
        r.id,
        r.slug,
        r.category,
        r.type,
        cultures.
          filter(c=>r.cultures.map(ci=>ci.slug)
          .includes(c.slug))
          .sort((a, b) => a.name.localeCompare(b.name)),
        clusters.find(c=>c.id===r.id));
    }).filter(cc=>cc.cluster);

  }
  generateCultureVoteScoreObjects(cultures:Culture[],votes:Vote[] = []) : CultureScore[]{ 

    const culturesForWhichWeHaveVotes = cultures.filter(culture => 
      votes.some(vote => vote.culture_ids && vote.culture_ids.includes(culture.id))
    );
    
    return culturesForWhichWeHaveVotes.map(c=> {
      const vote = votes.find(v => v.culture_ids && v.culture_ids.includes(c.id));
      return {
        culture_id : c.id,
        culture : c,
        score : vote.value,
        score2 : vote.value_2,
      }
    });

  }
  groupCultureScoreByProperty(cultureScores : CultureScore[], groupByProperty : string = 'score') : CultureScore[][]{ 

    debugger; // Seems like this function is not used anywhere. Should it be removed?

      const uniqueGroupByValues = Array.from(new Set(cultureScores.map(cultureScore => cultureScore[groupByProperty])));
      const groups = [];
      uniqueGroupByValues.forEach(value => {
        groups.push({
          value : value,
          items : cultureScores.filter(culture => culture[groupByProperty] === value)
        });
      });
      return groups.sort((a,b)=>a.value-b.value);


  }

  getCultureClustersData(culture_category: string, culture_type: string, cluster_category: string, cluster_type: string, freshFromServer: boolean): Observable<[Culture[], Cluster[], CultureClusterIdentifier[]]> {
    const cultures$ = this.getCultures(culture_category, culture_type, freshFromServer);
    const clusters$ = this.getClusters(cluster_category, cluster_type, freshFromServer);
    const cultureClusters$ = this.getCultureClusterRelationshipsData(cluster_category, cluster_type);
    return forkJoin([cultures$, clusters$, cultureClusters$]);
  }
  getCultures(category:string,type:string, freshFromServer:boolean) {
    this._requestedFilters.add(this.filterKey(category, type));
    let filteredCultures : Culture[] = [];
    if (category !== null && type !== null) {
      filteredCultures = this.cultures.filter(c => c.category === category && c.type === type);
    } else if (category !== null) {
      filteredCultures = this.cultures.filter(c => c.category === category && c.type === null);
    } else if (type !== null) {
      filteredCultures = this.cultures.filter(c => c.category === null && c.type === type);
    }
    if (filteredCultures?.length && !freshFromServer) {
      return of(filteredCultures); // .sort((a, b) => a.name.localeCompare(b.name)));
    }
    // Share an in-flight request for the same locale+filter so service-owned
    // repopulation and a component's own refetch on one locale change don't duplicate.
    const requestKey = `${this.activeLanguageKey}::${this.filterKey(category, type)}`;
    const inFlight = this._inFlightCultures.get(requestKey);
    if (inFlight) {
      return inFlight;
    }
    let url = 'api/v1/cultures?';
    url = category ? url+='category='+category:url;
    url = type ? url+='&type='+type:url;
    this._loadingObject.update(lo => ({...lo, 'cultures': true}));
    const request$ = this.http.get<{data:CultureResponse[]}>(url).pipe(
      tap(() => this._loadingObject.update(lo => ({...lo, 'cultures': false}))),
      map((response) => {
        if (response?.data?.length) {
          return this.cacheCultures(this.transformCultures(response.data));
        } else {
          return [];
        }
      }),
      catchError(error=>{
        this._loadingObject.update(lo => ({...lo, 'cultures': false}));
        return this.handleError(error);
      }),
      finalize(() => this._inFlightCultures.delete(requestKey)),
      shareReplay({ bufferSize: 1, refCount: false }),
      );
    this._inFlightCultures.set(requestKey, request$);
    return request$;
    }
    transformCultures(cultures:CultureResponse[]):Culture[]{
      return cultures.map(culture => {
        const c : Culture = {
          ...culture,
          metonyms: culture.metonyms ? culture.metonyms.split(',') : [],
          tag_id: 0, // deprecated
        };
        return c;
      });
    }
  getCulturesWithVariants(culture_ids: number[] = [], category:string,type:string, freshFromServer:boolean) {
    this._requestedFilters.add(this.filterKey(category, type));
    let filteredCultures : Culture[] = [];
    let filteredCultureMissingVariants : Culture[] = [];
    if (category !== null && type !== null) {
      filteredCultures = this.cultures.filter(c => c.variants && c.category === category && c.type === type && (culture_ids.length ? culture_ids.includes(c.id) : true));
      filteredCultureMissingVariants = this.cultures.filter(c => !c.variants && c.category === category && c.type === type && (culture_ids.length ? culture_ids.includes(c.id) : true));
    } else if (category !== null) {
      filteredCultures = this.cultures.filter(c => c.variants && c.category === category && c.type === null && (culture_ids.length ? culture_ids.includes(c.id) : true));
      filteredCultureMissingVariants = this.cultures.filter(c => !c.variants && c.category === category && c.type === null && (culture_ids.length ? culture_ids.includes(c.id) : true));
    } else if (type !== null) {
      filteredCultures = this.cultures.filter(c => c.variants && c.category === null && c.type === type && (culture_ids.length ? culture_ids.includes(c.id) : true));
      filteredCultureMissingVariants = this.cultures.filter(c => !c.variants && c.category === null && c.type === type && (culture_ids.length ? culture_ids.includes(c.id) : true));
    }
    if (!culture_ids.length) {
      if(filteredCultures.length && !filteredCultureMissingVariants.length && !freshFromServer){
        return of(filteredCultures)
      }
    } else {
      const filteredCultureIds = filteredCultures.map(c => c.id);
      if(this.dataProcessingService.matchTwoArrays(filteredCultureIds,culture_ids) && !freshFromServer){
        return of(filteredCultures)
      }
    }
    let url = 'api/v1/cultures-variants?';
    url = category ? url+='category='+category:url;
    url = type ? url+='&type='+type:url;
    return this.http.post<{data:CulturesWithVariantsResponse[]}>(url,{culture_ids}).pipe(
      map((response) => {
        if (response?.data?.length) {
          return this.cacheCultures(this.transformCultures(response.data));
        } else {
          return [];
        }
      }),
      catchError(error=>this.handleError(error))

      );
    }
    getCultureClusterRelationshipsData(category: string, type: string): Observable<CultureClusterIdentifier[]> {
      let cacheKey = category ? (type ? category + '-' + type : category) : 'culture-clusters';
      return from(caches.open('culture-clusters')).pipe(
        switchMap(cache => {
          return cache.match(cacheKey).then(async response => {
            let cacheExpired = false;
            if (response) {
              const cacheDate = response.headers.get('X-Cache-Date');
              let cacheAge = null;
              cacheAge = cacheDate ? new Date().getTime() - new Date(cacheDate).getTime() : null;
              const maximumCacheAge = this.maximumCacheAges.find(cacheAge => cacheAge.cacheKey === cacheKey)?.maxAge;
              if(!maximumCacheAge || cacheAge && cacheAge < maximumCacheAge ){ // About a month
                return response.json(); // as CultureClusterIdentifier[]; // Parse cached data
              } else {
                cache.delete(cacheKey);
                cacheExpired = true;
              }
            }
            if (cacheExpired || !response) {
              const url = `api/v1/culture-clusters?${category ? 'category=' + category : ''}${type ? '&type=' + type : ''}`;
              const responseData = await this.http.get<{ data: CultureClusterIdentifier[] }>(url).toPromise();
              // Cache the response
                const cacheResponse = new Response(JSON.stringify(responseData.data));
                const cacheDate = new Date();
                cacheResponse.headers.append('X-Cache-Date', cacheDate.toISOString());
                await cache.put(cacheKey, cacheResponse);
              return responseData.data;
            }
          });
        }),
        catchError(error => {
          return throwError(error);
        })
      );
    }
    
    private handleError(errorResponse: HttpErrorResponse) {
      let errorMessage = 'error.something_went_wrong';
      if (!errorResponse.error || !errorResponse.error.message) {
        return throwError(errorMessage);
      }
      // if (errorResponse.error.errors?.slug?.[0] === 'The slug has already been taken.'){
      //   errorMessage ="content_management.slug_availability_error";
      //   return throwError(errorMessage);
      // }
      const message = errorResponse.error.message;
      const standardErrorMessageTranslationKey = this.errorService.getCommonErrorMessageTranslationKey(message);
      if(standardErrorMessageTranslationKey){
        errorMessage = standardErrorMessageTranslationKey;
      }
      if (errorResponse.error.meta){
        return throwError({message:errorMessage,meta:errorResponse.error.meta});
      }
      return throwError(errorMessage);
    }
}
